
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { BiCommentAdd, BiEdit, BiMessage, BiMessageAltDetail, BiUser } from 'react-icons/bi';
import { FaCircle, FaClockRotateLeft } from "react-icons/fa6";
import { BsInfoCircle } from 'react-icons/bs';
import { MdOutlineAddComment } from 'react-icons/md';
import { toast } from 'react-toastify';
import { AppEventContext, LoaderContext, SidebarContext } from '../../App';
import DateAnswerField from '../../components/Answer/DateAnswerField';
import DateRangeAnswerField from '../../components/Answer/DateRangeAnswerField';
import FileChooserAnswerField from '../../components/Answer/FileChooserAnswerField';
import MultiSelectAnswerField from '../../components/Answer/MultiSelectAnswerField';
import MultipleChoiceAnswerField from '../../components/Answer/MultipleChoiceAnswerField';
import RadioAnswerField from '../../components/Answer/RadioAnswerField';
import SingleLineAnswerField from '../../components/Answer/SingleLineAnswerField';
import SingleSelectAnswerField from '../../components/Answer/SingleSelectAnswerField';
import TextBoxAnswerField from '../../components/Answer/TextBoxAnswerField';
import FileListPanel from '../../components/FilePreview/FileListPanel';
import FormFooter from '../../components/Form/FormFooter';
import GridQuestionView from '../../components/GridQuestionView/GridQuestionView';
import GridTable from '../../components/RadioGridView/GridTable';
import DataTable from '../../components/TableView/DataTable';
import ResizablePanel from '../../components/layout/ResizablePanel';
import { usePanelResize } from '../../hooks/usePanelResize';
import { FORM_REVIEW_APPROVED_EVENT, FORM_REVIEW_REJECTED_EVENT, FormStatus, LS_ACCESS_TOKEN_KEY, QuestionType } from '../../core/constants/common';
import FileService from '../../core/services/FileService';
import FormService from '../../core/services/FormService';
import FormEditService from '../../core/services/FromEditService';
import ConfirmDialog from '../../core/utilities/ConfirmPopup';
import Utils from '../../core/utilities/Utils';
import { flattenFormFields, getInputTypeForFieldType } from '../../core/utilities/gridQuestionUtils';
import { getVisibleQuestionNumber } from '../../core/utilities/questionNumbering';
import ReviewCommentModal from '../FormReview/ReviewCommentModal';
import SubmittedResponseTreeNavigation from './SubmittedResponseTreeNavigation';
import './FormSubmit.scss';
import { BsChatDots, BsDot, BsQuestionCircle } from 'react-icons/bs';
import '../../design-system/index.scss';
import '../../components/ui/ui.scss';
import '../../components/layout/builder-shell.scss';
import '../../components/layout/preview-shell.scss';


const SubmittedResponseView = ({
	token,
	form,
	submittedData,
	isFormInReview = false,
	unitsOfMeasure,
	onSubmit,
	onRefreshSubmittedData,
	isSinglePageView,
	onFilePreview,
	onReadOCRData
}) => {

	const [activeSection, setActiveSection] = useState(null);
	const [activeSectionIndex, setActiveSectionIndex] = useState(0);
	const [chosenLocationInSection, setChosenLocationInSection] = useState(0);
	const [hasSubmForCurSection, setHasSubmForCurSection] = useState(false);
	const [reviewComments, setReviewComments] = useState([]);
	const [openReviewComment, setOpenReviewComment] = useState({});
	const [editedAnswer, setEditedAnswer] = useState({ formField: { id: 0 } });
	const { setLoaderState } = useContext(LoaderContext);
	const { appEventData } = useContext(AppEventContext);
	// Same source of truth the old FormSidebar used, so the app-wide sidebar
	// toggle button (App.js) keeps working for this page's sidebar too.
	const { sidebarDisplay: sidebarOpen = true } = useContext(SidebarContext) || {};
	const { widths, setSidebarWidth } = usePanelResize();
	const [showComment, setShowComment] = useState({});
	const [fileUploadEnabled, setFileUploadEnabled] = useState({});
	const [confirmDeleteFile, setConfirmDeleteFile] = useState({ show: false, fileId: null, fileIndex: null, fileName: null });
	const accessToken = localStorage.getItem(LS_ACCESS_TOKEN_KEY);
	const numericInputRef = useRef(null);
	const [visiblePopovers, setVisiblePopovers] = useState({});

	// Block wheel events on numeric input
	useEffect(() => {
		const input = numericInputRef.current;
		if (input) {
			const handleWheel = (e) => {
				e.preventDefault();
				e.stopPropagation();
				return false;
			};

			input.addEventListener('wheel', handleWheel, { passive: false });
			return () => {
				input.removeEventListener('wheel', handleWheel);
			};
		}
	}, [editedAnswer.formField.id]); // Re-run when the field changes

	useEffect(() => {
		if (activeSection && form && submittedData) {
			// If there is already an active section, do not reset. 
			return;
		}
		setActiveSection(
			form?.fields?.length > 0
				? { fields: form?.fields ?? [], subSections: [] }
				: form?.sections[0] ?? null
		);
		setActiveSectionIndex(form?.fields?.length > 0 ? 0 : 1);
		const activeSectionId = form?.fields?.length > 0
			? 0
			: form?.sections[0]?.id ?? 0;
		setHasSubmForCurSection(hasSubmissionForTheCurrentSection(activeSectionId, submittedData?.sectionSubmissions));
		setActiveLocation(submittedData?.sectionSubmissions, activeSectionId);
		fetchReviewComments();
	}, [form, submittedData]);

	useEffect(() => {
		if (appEventData.name !== FORM_REVIEW_APPROVED_EVENT
			&& appEventData.name !== FORM_REVIEW_REJECTED_EVENT) return;

		onRefreshSubmittedData();
	}, [appEventData]);

	const changeSection = (sectionId) => {
		const sectionIndex = form?.sections?.findIndex(s => s.id === sectionId);
		if (sectionIndex < 0) return;

		setActiveSection(form?.sections[sectionIndex]);
		setActiveSectionIndex(sectionIndex + 1);
		setHasSubmForCurSection(
			hasSubmissionForTheCurrentSection(form?.sections[sectionIndex]?.id, submittedData?.sectionSubmissions)
		);
	};

	const changeSubSection = (subSectionId) => {
		const sectionId = getRootSectionIdForGivenSection(form?.sections, subSectionId);
		const sectionIndex = form?.sections?.findIndex(s => s.id === sectionId);
		if (sectionIndex < 0) return;

		setActiveSection(form?.sections[sectionIndex]);
		setActiveSectionIndex(sectionIndex + 1);
		setHasSubmForCurSection(
			hasSubmissionForTheCurrentSection(form?.sections[sectionIndex]?.id, submittedData?.sectionSubmissions)
		);
	};

	const getRootSectionIdForGivenSection = (sections, sectionId) => {
		let rootSection = 0;
		for (let index = 0; index < sections.length; index++) {
			const section = sections[index];
			if (section.id === sectionId) {
				rootSection = section.id;
				break;
			}
			if (section.subSections?.length > 0
				&& getRootSectionIdForGivenSection(section.subSections, sectionId) > 0) {
				rootSection = section.id;
				break;
			}
		}
		return rootSection;
	};

	const scrollToSection = (sectionId) => {
		setTimeout(() => {
			const el = document.getElementById(`section-${sectionId}`);
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	};

	const scrollToField = (fieldId) => {
		setTimeout(() => {
			const el = document.querySelector(`[data-field-id="${fieldId}"]`);
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 150);
	};
	// Same response-lookup rule `renderAnswerText` uses for the field being
	// rendered directly, reused here to resolve each GRID cell's linked field
	// (which lives elsewhere on the form, not inside the grid's own response).
	const getResponseForField = (fieldId) => {
		if (hasSubmForCurSection && chosenLocationInSection > 0) {
			const sectionReponses = submittedData?.sectionSubmissions?.find(s =>
				s.section.id === activeSection?.id && s.branch.id === chosenLocationInSection)?.responses ?? [];
			return sectionReponses?.find(res => res.formField.id === fieldId);
		}
		return submittedData?.responses?.find(d => d.formField.id === fieldId);
	};

	const getAnswer = (response, field) => {

		if (!response) return '';

		if (field.type === QuestionType.SINGLE_TEXTBOX
			|| field.type === QuestionType.TEXT_BOX) {
			return response.textResponse ?? '';
		}
		if (field.type === QuestionType.NUMERIC_VALUE) {
			if (response.uom === "$") {
				return `${response.uom ?? ''} ${response.numericResponse ?? ''} `;
			} else {
				return `${response.numericResponse ?? ''} ${response.uom ?? ''}`;
			}

		}
		if (field.type === QuestionType.DROP_DOWN
			|| field.type === QuestionType.RADIO) {
			const responseId = response.responseOptions[0]?.id ?? 0;
			if (!responseId) return '';

			return field.options?.find(o => o.id === responseId)?.name ?? '';
		}
		if (field.type === QuestionType.DATE_TIME_BOX) {
			return response.dateResponse ?? '';
		}
		if (field.type === QuestionType.DATE_RANGE) {
			return `${response.dateResponse ?? ''} to ${response.toDateResponse ?? ''}`;
		}
		if (field.type === QuestionType.FILE_UPLOAD) {
			const files = [];
			response.files?.forEach((f, fIndex) => {
				files.push(
					<a onClick={() => downloadFile(f.id)}
						key={`fileAnswerResponse-${fIndex}`}
						className='text-primary cursor-pointer'>
						<span>{f.fileName}</span>
					</a>
				);
			})
			return files;
		}
		if (field.type === QuestionType.MULTIPLE_CHOICE
			|| field.type === QuestionType.MULTI_SELECT_DROP_DOWN) {
			let values = '';
			field.options?.forEach(o => {
				if (response.responseOptions?.findIndex(r => r.id === o.id) < 0) return;

				values += `${o.name}, `;
			});
			return values !== ''
				? values.substring(0, values.length - 2)
				: values;
		}
		if (field.type === QuestionType.RADIO_GRID) {
			return (
				<GridTable
					type='SUBMIT_RESPONSE'
					content={field}
					responseData={response}
				/>
			);
		}
		if (field.type === QuestionType.TABLE) {
			return (
				<DataTable
					type='SUBMIT_RESPONSE'
					content={field}
					responseData={response}
				/>
			);
		}
		if (field.type === QuestionType.GRID) {
			return (
				<GridQuestionView
					content={field}
					answerContext={{
						readOnly: true,
						getDisplayValue: (linkedFieldId) => {
							const linkedField = flattenFormFields(form).find(f => f.id === linkedFieldId);
							if (!linkedField) return '';
							return getAnswer(getResponseForField(linkedFieldId), linkedField);
						},
					}}
				/>
			);
		}
		return '';
	};

	const nextSection = () => {
		const index = activeSectionIndex + 1;
		if (index < 0) return;

		setActiveSection(form?.sections[index - 1]);
		setActiveSectionIndex(index);
		setHasSubmForCurSection(
			hasSubmissionForTheCurrentSection(form?.sections[index - 1]?.id, submittedData?.sectionSubmissions)
		);
	};

	const previousSection = () => {
		const index = activeSectionIndex - 1;
		if (index < 0) return;

		if (index === 0) {
			setActiveSection({ fields: form?.fields ?? [], subSections: [] });
		} else {
			setActiveSection(form?.sections[index - 1]);
			setHasSubmForCurSection(
				hasSubmissionForTheCurrentSection(form?.sections[index - 1]?.id, submittedData?.sectionSubmissions)
			);
		}
		setActiveSectionIndex(index);
	};

	const hasSubmissionForTheCurrentSection = (currentSectionId, submissions) => {
		if (!currentSectionId) return false;

		let hasSubmission = false;
		for (let index = 0; index < submissions?.length; index++) {
			const sectionId = submissions[index]?.section?.id ?? -1;
			if (sectionId === -1) continue;

			if (sectionId === currentSectionId) {
				hasSubmission = true;
				break;
			}
			const section = form.sections?.find(s => s.id === sectionId);
			if (!section) continue;

			const matchIndex = section.subSections?.findIndex(s => s.id === currentSectionId);
			if (matchIndex === -1) continue;

			hasSubmission = true;
			break;
		}
		return hasSubmission;
	};

	const getLocationsForSectionSubmission = () => {
		const branches = [];
		submittedData?.sectionSubmissions?.forEach(s => {
			if (s.section.id !== activeSection?.id) return;

			branches.push({ id: s.branch.id, name: s.branch.name });
		});
		return branches;
	};

	const setActiveLocation = (submissions, sectionId) => {
		const locations = [];
		submissions?.forEach(s => {
			if (s.section.id !== sectionId) return;

			locations.push({ id: s.branch.id, name: s.branch.name });
		});
		setChosenLocationInSection(locations?.length > 0 ? locations[0]?.id : 0)
	};

	const toggleHelpTextPopover = (id) => {
		setVisiblePopovers((prevVisiblePopovers) => ({
			...prevVisiblePopovers,
			[id]: !prevVisiblePopovers[id],
		}));
	};

	const popoverClickRootClose = (question) => (
		<Popover id="popover-trigger-click-root-close">
			<Popover.Header>
				Help
			</Popover.Header>
			<Popover.Body className=''>
				{question.fieldHelpText && (
					<p>
						<div dangerouslySetInnerHTML={{ __html: question.fieldHelpText }} />
					</p>
				)}
			</Popover.Body>
		</Popover>
	);

	const renderQuestionCard = (field, index, fields) => {
		const questionNumber = getVisibleQuestionNumber(fields, index);
		return (
			<div className='builder-question-card' data-field-id={field.id} key={`question-${field.id}`}>
				<div className='PS_inner_01'>
					<div className='PS_inner_Qus_ViewCard02'>
						<div className='PSS_Qus_ViewCard02'>
							<div className='PSinner_Qus_View_box02'>
								<div className='d-flex'>
									<h6 className='mb-0 me-1'>
										{questionNumber}.
									</h6>
									<div>
										<h6 className='mb-0'>
											{field.name}
											{!field.optional && <span className="text-danger h5 m-0" style={{ lineHeight: '1px' }}> *</span>}
											{
												field.fieldHelpText &&

												<OverlayTrigger
													trigger="click"
													rootClose
													placement="auto"
													overlay={popoverClickRootClose(field)}
													onToggle={() => toggleHelpTextPopover(field.id)}
												>
													<span className='mx-2 cursor' onClick={() => toggleHelpTextPopover(field.id)}>
														<BsQuestionCircle />
													</span>
												</OverlayTrigger>
											}
										</h6>
										{renderAnswerText(field)}
									</div>
								</div>

							</div>

						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderFieldList = (fields) => (
		<div className='builder-field-list'>
			{
				fields?.map((field, index) => !field?.hide_alt && renderQuestionCard(field, index, fields))
			}
		</div>
	);

	const renderSectionAuditLog = () => {
		return (
			<OverlayTrigger placement="top"
				overlay={Utils.getSectionAuditLogTooltip(submittedData)}>
				<span className='AuditLog_section 2'>
					<FaClockRotateLeft />
				</span>
			</OverlayTrigger>
		);
	}
	const renderSubsectionCards = (sections, sectionIndex) => {
		return sections?.filter((section) => !section.hide_alt).map((section, index) => (
			<div id={`section-${section.id}`}
				key={`section-${section.id}`}
				className='builder-section-card builder-section-card--subsection'
				style={{ scrollMarginTop: '90px' }}>
				<div className='builder-section-card__header'>
					<span className='builder-section-card__title'>
						{sectionIndex}.{index + 1}. {section.title}
					</span>
					{section.description &&
						<OverlayTrigger
							placement="top"
							overlay={<Tooltip id={`tooltip-${section.id}`} className="custom-tooltip">{section.description}</Tooltip>}
						>
							<span className="builder-section-card__count">
								<BsInfoCircle />
							</span>
						</OverlayTrigger>
					}
				</div>
				<div className='builder-section-card__body'>
					{section?.fields?.length > 0 && renderFieldList(section.fields)}
					{
						section?.subSections?.length > 0 &&
						<div className='builder-document__subsections'>
							{renderSubsectionCards(section?.subSections, `${sectionIndex}.${index + 1}`)}
						</div>
					}
				</div>
			</div>
		));
	};
	const renderLocationSelector = () => {
		if (!hasSubmForCurSection) {
			return Utils.getBranchOrGroupDetailsToRender(form);
		}
		return (
			<div className='mx-2'>
				<select className='form-select w-auto'
					value={chosenLocationInSection}
					onChange={(e) => setChosenLocationInSection(+e.target.value)}>
					<option value={null}>Select</option>
					{
						getLocationsForSectionSubmission()?.map(location => (
							<option key={location.id} value={location.id}>{location.name}</option>
						))
					}
				</select>
			</div>
		)
	};

	const renderCommentOption = (field, hasComments = false) => {
		if (isFormInReview && submittedData?.status !== FormStatus.PENDING_APPROVAL) return;

		if (!isFormInReview && submittedData?.status !== FormStatus.CORRECTION_PENDING) return;

		let response;
		if (hasSubmForCurSection && chosenLocationInSection > 0) {
			const sectionReponses = submittedData?.sectionSubmissions?.find(s =>
				s.section.id === activeSection?.id && s.branch.id === chosenLocationInSection)?.responses ?? [];
			response = sectionReponses?.find(res => res.formField.id === field.id);
		} else {
			response = submittedData?.responses?.find(d => d.formField.id === field.id);
		}
		return (
			<div className="comment-item add-comment-item">
				<div className="comment-header">
					<div className="comment-avatar">
						<div className="avatar-placeholder">
						</div>
					</div>
					<div className="comment-meta">
						<OverlayTrigger placement="top"
							overlay={<Tooltip><span>{`Please click the icon to enter your ${isFormInReview ? 'review' : ''} comments`}</span></Tooltip>}>
							<span className="comment-author">
								<BiCommentAdd className='text-primary me-1' />
								<a href="javascript:void(0);"
									className="add-comment-link"
									onClick={() => setOpenReviewComment({
										responseId: response?.id,
										token: token,
										comment: null,
										isFormInReview: isFormInReview,
										id: 0
									})}>
									Add a comment
								</a>
							</span>
						</OverlayTrigger>
					</div>
				</div>
				{hasComments && <div className="comment-connector"></div>}
			</div>
		);
	};

	const reviewCommentModalCallback = (val) => {
		setOpenReviewComment({});
		if (!val) return;

		fetchReviewComments();
	};

	const fetchReviewComments = () => {
		// if (submittedData?.status !== FormStatus.PENDING_APPROVAL
		// 	&& submittedData?.status !== FormStatus.CORRECTION_PENDING) return;

		FormService.getReviewComments(token, isFormInReview)
			.then(res => {
				if (res.status !== 200) return;

				setReviewComments(res.data);
			}).catch(err => { });
	};
	const renderAnswerText = (field) => {
		let response;
		if (hasSubmForCurSection && chosenLocationInSection > 0) {
			const sectionReponses = submittedData?.sectionSubmissions?.find(s =>
				s.section.id === activeSection?.id && s.branch.id === chosenLocationInSection)?.responses ?? [];
			response = sectionReponses?.find(res => res.formField.id === field.id);
		} else {
			response = submittedData?.responses?.find(d => d.formField.id === field.id);
		}
		let comments = reviewComments?.filter(c => c.responseId === response?.id) ?? [];
		const answer = getAnswer(response, field);
		let historicalAnswers = [];
		if (form.historicalDataSupported && field.historicalDataSupported) {
			form.historicalDataYears?.forEach(year => {
				const histDataForYear = submittedData?.linkedSubmissions?.find(s => s.period?.split('-')[0] === `${year}`);
				const histAns = histDataForYear?.responses?.find(r => r.formField.id === field.id);
				let numAns = '';
				if (histAns) {
					if (histAns.uom === "$") {
						numAns = `${histAns.uom ?? ''} ${histAns.numericResponse ?? ''}`;
					} else {
						numAns = `${histAns.numericResponse ?? ''} ${histAns.uom ?? ''}`;
					}
				}
				historicalAnswers.push({
					year: year,
					answer: numAns
				});
			});
		}
		const reportingYear = submittedData?.assessment?.period ? submittedData.assessment.period.split('-')[0] ?? null : null;
		comments = comments?.sort((a, b) => {
			// Get the date to use for sorting, preferring lastEditedOn, then falling back to createdDate
			const dateA = a.lastEditedOn || a.createdDate;
			const dateB = b.lastEditedOn || b.createdDate;
			const timeA = moment(dateA).valueOf();
			const timeB = moment(dateB).valueOf();
			return timeB - timeA;
		});
		return (
			<>
				{
					editedAnswer.formField.id === field.id
						? renderAnswerField(field)
						:
						<>
							{
								form.historicalDataSupported && field.historicalDataSupported
									?
									<p className='d-flex justify-content-between align-items-center text-normal mt-2'><span>{getAnswerToRender(answer, field.type)}</span><span className='ms-4 badge bg-secondary'>{reportingYear}</span></p>
									:
									<p className='text-normal mb-0 mt-1'>{getAnswerToRender(answer, field.type)}</p>
							}
							{
								historicalAnswers?.map(h => {
									return <p className='d-flex justify-content-between align-items-center text-normal mt-2'><span>{getAnswerToRender(h.answer, field.type)}</span><span className='ms-4 badge bg-secondary'>{h.year}</span></p>
								})
							}
						</>
				}

				{

					!isFormInReview
					&& submittedData?.status === FormStatus.CORRECTION_PENDING
					&& !editedAnswer.formField.id
					&& comments.length > 0 &&
					<button className='btn btn-primary mt-2'
						onClick={() => onEditResponse(field, response)}>Edit Response</button>
				}
				{
					!isFormInReview
					&& submittedData?.status === FormStatus.CORRECTION_PENDING
					&& editedAnswer.formField.id === field.id
					&& comments.length > 0 &&
					<>
						<button className='btn btn-secondary sbmBtn mt-2'
							onClick={() => setEditedAnswer({ formField: { id: 0 } })}>Cancel</button>
						<button className='btn btn-primary sbmBtn mx-2 mt-2'
							onClick={() => saveAnswer()}>Save</button>
					</>
				}

				{field.allowRemark && (renderCommentIcon(field.id))}
				{
					field.allowFileUpload &&
					(
						editedAnswer.formField.id === field.id
							? renderFileUploadAttachment()
							: renderFileIcon(field.id)
					)
				}

				{
					<div className="comments-section mt-3">
						{comments.length > 0 &&
							<div className="comments-header">
								<h6 className="comments-title mb-2">Comments</h6>
							</div>
						}
						<div className="comment-thread">
							{renderCommentOption(field, true)}
							{comments.map((comment, index) => {
								const isLastComment = index === comments.length - 1;
								return (
									<div key={comment.id} className="comment-item">
										<div className="comment-header">
											<div className="comment-avatar">
												<div className="avatar-placeholder">
												</div>
											</div>
											<div className="comment-meta">
												<span className="comment-author">{renderUserInfo(comment)}</span>
												<span className="comment-timestamp">
													{comment.lastEditedOn
														? moment(comment.lastEditedOn).format('DD MMMM YYYY, h.mm a')
														: moment(comment.createdDate).format('DD MMMM YYYY, h.mm a')}
												</span>
											</div>
											<div className="comment-actions">
												{comment.editable &&
													<span className='edit-comment cursor'
														onClick={() =>
															setOpenReviewComment({
																responseId: response?.id,
																token: token,
																comment: comment.comment,
																isFormInReview: isFormInReview,
																id: comment.id
															})}><BiEdit /></span>
												}
											</div>
										</div>
										<div className="comment-content text-danger">
											<div className="comment-icon me-1">
												<BiMessage />
											</div>
											<div className="comment-text">
												{comment.comment}
											</div>
										</div>
										{!isLastComment && <div className="comment-connector"></div>}
									</div>
								);
							})}
						</div>
					</div>
				}

			</>
		);
	};

	const onEditResponse = (field, response) => {
		let answer = { formField: { id: field.id } };
		if (field.type === QuestionType.NUMERIC_VALUE) {
			answer = {
				...answer,
				numericResponse: response.numericResponse,
				uom: response.uom
			};
		} else if (field.type === QuestionType.TEXT_BOX
			|| field.type === QuestionType.SINGLE_TEXTBOX) {
			answer = {
				...answer,
				textResponse: response.textResponse
			};
		} else if (field.type === QuestionType.RADIO
			|| field.type === QuestionType.DROP_DOWN) {
			answer = {
				...answer,
				responseOptions: response.responseOptions
			};
		} else if (field.type === QuestionType.MULTI_SELECT_DROP_DOWN
			|| field.type === QuestionType.MULTIPLE_CHOICE) {
			const resOptions = [], optionsToShow = [];
			response.responseOptions?.forEach(r => {
				resOptions.push({ id: r.id });
				const opt = field.options?.find(o => o.id === r.id);
				optionsToShow.push(opt);
			});
			answer = {
				...answer,
				responseOptions: resOptions
			};
			if (field.type === QuestionType.MULTI_SELECT_DROP_DOWN) {
				answer['responseOptionsShow'] = optionsToShow;
			}
		} else if (field.type === QuestionType.DATE_TIME_BOX
			|| field.type === QuestionType.DATE_RANGE) {
			answer = {
				...answer,
				dateResponse: response.dateResponse
			};
			if (field.type === QuestionType.DATE_RANGE) {
				answer['toDateResponse'] = response.toDateResponse;
			}
		} else if (field.type === QuestionType.FILE_UPLOAD) {
			answer = {
				...answer,
				files: response.files
			};
		} else if (field.type === QuestionType.TABLE) {
			answer = {
				...answer,
				tableRowResponses: response.tableRowResponses
			};
		} else if (field.type === QuestionType.GRID) {
			// A GRID question never owns an answer itself — each cell's value lives
			// under its linked field's own response — so editing a GRID means
			// editing every linked cell's response together, keyed by linked field id.
			const linkedFieldIds = (field.gridQuestionConfig?.cellLinks || [])
				.map(link => link.linkedFieldId)
				.filter(Boolean);
			const gridCellResponses = {};
			linkedFieldIds.forEach(linkedFieldId => {
				const linkedResponse = getResponseForField(linkedFieldId);
				gridCellResponses[linkedFieldId] = {
					formField: { id: linkedFieldId },
					numericResponse: linkedResponse?.numericResponse,
					textResponse: linkedResponse?.textResponse,
					uom: linkedResponse?.uom
				};
			});
			answer = {
				...answer,
				gridCellResponses
			};
		}
		// Files should be carried over for editing regardless of question type,
		// since attachments (allowFileUpload) can be added to any field, not just FILE_UPLOAD questions
		if (field.allowFileUpload) {
			answer['files'] = response.files;
		}
		// Remark response should not be reset on editing
		answer['remarkResponse'] = response.remarkResponse;
		setEditedAnswer(answer);
	};
	const renderAnswerField = (field) => {
		return (
			<div>
				{
					field.type === 'NUMERIC_VALUE' &&
					<div className='w-100 d-flex'>
						<div style={{ width: field.uomRequired ? '100%' : '100%' }}>
							<input type="number"
								ref={numericInputRef}
								className="form-control"
								value={editedAnswer.numericResponse ?? ''}
								name={field.id}
								onChange={(e) => setEditedAnswer({ ...editedAnswer, formField: { id: field.id }, numericResponse: e.target.value })}
							/>
						</div>
						{
							field.uomRequired &&
							<div className='ms-2' style={{ width: '20%' }}>
								<select className='form-select w-auto'
									value={editedAnswer.uom}
									onChange={(e) => setEditedAnswer({ ...editedAnswer, uom: e.target.value })}>
									<option key='' value=''>Select</option>
									{
										(field.relevantUoms?.length > 0
											? field.relevantUoms.sort((a, b) => a.localeCompare(b))
											: unitsOfMeasure
										)?.map(u => {
											return <option key={u} value={u}>{u}</option>
										})
									}
									{
										field.relevantUoms?.length > 0
											?
											field.relevantUoms.sort((a, b) => a.localeCompare(b))?.map(u => {
												return (<option key={u} value={u}>
													{unitsOfMeasure?.length > 0 ? unitsOfMeasure?.find(unit => unit?.value === u)?.label : u}
												</option>
												)
											})
											:
											unitsOfMeasure?.map(u => {
												return <option key={u.value} value={u.value}>{u.label}</option>
											})
									}
								</select>
							</div>
						}
					</div>
				}
				{
					field.type === QuestionType.SINGLE_TEXTBOX &&
					<SingleLineAnswerField
						fieldValue={editedAnswer.textResponse}
						onAnswerChange={(val) => setEditedAnswer({
							formField: { id: field.id }, textResponse: val
						})}
					/>
				}
				{
					field.type === QuestionType.TEXT_BOX &&
					<TextBoxAnswerField
						fieldValue={editedAnswer.textResponse}
						onAnswerChange={(val) => setEditedAnswer({
							formField: { id: field.id }, textResponse: val
						})}
					/>
				}
				{
					field.type === QuestionType.RADIO &&
					<RadioAnswerField options={field.options}
						fieldValue={editedAnswer.responseOptions[0]?.id}
						onAnswerChange={(val) => setEditedAnswer({
							formField: {
								id: field.id,
							},
							responseOptions: [{
								id: +val,
								name: field.options?.find(o => o.id === (+val))?.name
							}]
						})}
					/>
				}
				{
					field.type === QuestionType.DROP_DOWN &&
					<SingleSelectAnswerField options={field.options}
						fieldValue={editedAnswer.responseOptions[0]?.id}
						onAnswerChange={(val) => setEditedAnswer({
							formField: {
								id: field.id,
							},
							responseOptions: [{
								id: +val,
								name: field.options?.find(o => o.id === (+val))?.name
							}]
						})}
					/>
				}
				{
					field.type === QuestionType.MULTI_SELECT_DROP_DOWN &&
					<MultiSelectAnswerField options={field.options}
						selectedOptions={editedAnswer.responseOptionsShow}
						onAnswerChange={(val1, val2) => onMultiSelectionChange(val1, val2, field)}
					/>
				}
				{
					field.type === QuestionType.MULTIPLE_CHOICE &&
					<MultipleChoiceAnswerField options={field.options}
						selectedOptions={editedAnswer.responseOptions}
						onAnswerChange={(val) => onMultipleChoiceValChange(+val, field)}
					/>
				}
				{
					field.type === QuestionType.DATE_TIME_BOX &&
					<DateAnswerField
						fieldValue={editedAnswer.dateResponse}
						onAnswerChange={(val) => setEditedAnswer({
							formField: { id: field.id }, dateResponse: val
						})}
					/>
				}
				{
					field.type === QuestionType.DATE_RANGE &&
					<DateRangeAnswerField
						startValue={editedAnswer.dateResponse}
						endValue={editedAnswer.toDateResponse}
						onStartChange={(val) => setEditedAnswer({
							...editedAnswer, formField: { id: field.id }, dateResponse: val
						})}
						onEndChange={(val) => setEditedAnswer({
							...editedAnswer, formField: { id: field.id }, toDateResponse: val
						})}
					/>
				}
				{
					field.type === QuestionType.FILE_UPLOAD &&
					<FileChooserAnswerField
						files={editedAnswer.files}
						onFileSelect={(evt) => handleFileSelect(evt)}
						onFileDownload={(fid) => downloadFile(fid)}
						onFileDelete={(fid, fIndex, fileName) => requestRemoveFile(fid, fIndex, fileName)}
					/>
				}
				{
					field.type === QuestionType.TABLE &&
					<DataTable
						type='SUBMIT'
						content={field}
						responseData={editedAnswer}
						onRowsChange={(rows) => setEditedAnswer({
							...editedAnswer, formField: { id: field.id }, tableRowResponses: rows
						})}
					/>
				}
				{
					field.type === QuestionType.GRID &&
					<GridQuestionView
						content={field}
						answerContext={buildGridEditAnswerContext()}
					/>
				}
			</div>
		);
	};

	// Mirrors the read-only GRID answerContext in `getAnswer`, but wired to
	// `editedAnswer.gridCellResponses` so each cell edits its linked field's
	// response in place, ahead of a single Save that submits them all together.
	const buildGridEditAnswerContext = () => {
		const allFields = flattenFormFields(form);
		return {
			readOnly: false,
			getDisplayValue: (linkedFieldId) => {
				const linkedField = allFields.find(f => f.id === linkedFieldId);
				const cell = editedAnswer.gridCellResponses?.[linkedFieldId];
				if (!linkedField || !cell) return '';
				return linkedField.type === QuestionType.NUMERIC_VALUE
					? (cell.numericResponse ?? '')
					: (cell.textResponse ?? '');
			},
			getInputType: (linkedFieldId) => getInputTypeForFieldType(allFields.find(f => f.id === linkedFieldId)?.type),
			getChangeHandler: (linkedFieldId) => (e) => {
				const linkedField = allFields.find(f => f.id === linkedFieldId);
				const value = e.target.value;
				setEditedAnswer(prev => ({
					...prev,
					gridCellResponses: {
						...prev.gridCellResponses,
						[linkedFieldId]: {
							...prev.gridCellResponses?.[linkedFieldId],
							formField: { id: linkedFieldId },
							...(linkedField?.type === QuestionType.NUMERIC_VALUE
								? { numericResponse: value }
								: { textResponse: value })
						}
					}
				}));
			},
			renderExtra: (linkedFieldId) => {
				const linkedField = allFields.find(f => f.id === linkedFieldId);
				if (!linkedField || linkedField.type !== QuestionType.NUMERIC_VALUE || !linkedField.uomRequired) return null;
				const cell = editedAnswer.gridCellResponses?.[linkedFieldId];
				const relevantUoms = linkedField.relevantUoms;
				return (
					<select className='form-select'
						value={cell?.uom ?? ''}
						onChange={(e) => setEditedAnswer(prev => ({
							...prev,
							gridCellResponses: {
								...prev.gridCellResponses,
								[linkedFieldId]: {
									...prev.gridCellResponses?.[linkedFieldId],
									formField: { id: linkedFieldId },
									uom: e.target.value
								}
							}
						}))}>
						<option value=''>Select</option>
						{relevantUoms?.length > 0
							? [...relevantUoms].sort((a, b) => a.localeCompare(b)).map(u => (
								<option key={u} value={u}>
									{unitsOfMeasure?.length > 0 ? unitsOfMeasure.find(unit => unit?.value === u)?.label : u}
								</option>
							))
							: unitsOfMeasure?.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
					</select>
				);
			},
		};
	};

	const saveAnswer = (answerToSave = editedAnswer) => {
		const payload = {
			responses: answerToSave.gridCellResponses
				? Object.values(answerToSave.gridCellResponses)
				: [answerToSave],
			submissionId: submittedData.submissionId
		};
		setLoaderState({ display: true });
		FormEditService.submitForm(token, payload, false, false)
			.then(res => {
				setLoaderState({ display: false });
				setEditedAnswer({ formField: { id: 0 } });
				if (res.status !== 200 && res.status !== 201) return;

				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				onRefreshSubmittedData();
			}).catch(err => {
				setLoaderState({ display: false });
			})
	};

	const downloadFile = (id) => {
		FileService.downloadFile({
			token: token,
			fileId: id,
			submissionId: accessToken ? submittedData.id : '',
			isReview: isFormInReview
		}).then((response) => {
			const fileData = `data:${response.data.contentType};base64,${response.data.content}`;
			const link = document.createElement("a");
			link.href = fileData;
			link.download = response.data.fileName;
			link.click();
		}).catch((error) => { });
	};

	const onMultiSelectionChange = (selectedList, selectedItem, field) => {
		let mulObjArr = [];
		for (let i = 0; i < selectedList.length; i++) {
			mulObjArr.push({
				id: parseInt(selectedList[i].id),
				name: field.options?.find(o => o.id === +selectedList[i].id)?.name
			});
		}
		setEditedAnswer({
			formField: {
				id: field.id,
			},
			responseOptions: mulObjArr,
			responseOptionsShow: selectedList
		});
	};

	const onMultipleChoiceValChange = (val, field) => {
		const existingOptions = JSON.parse(JSON.stringify(editedAnswer.responseOptions));
		const index = existingOptions.findIndex(x => x.id === val);
		if (index >= 0) {
			existingOptions.splice(index, 1);
		} else {
			const opt = {
				id: val,
				name: field.options?.find(o => o.id === (val))?.name
			};
			existingOptions.push(opt);
		}
		setEditedAnswer({
			formField: {
				id: field.id,
			},
			responseOptions: existingOptions
		});
	};

	const handleFileSelect = (event) => {
		const filesUploaded = Array.from(event.target.files);
		filesUploaded.forEach((fileUploaded) => {
			if (fileUploaded.size > 1024 * 1024 * 2) {
				toast.warning('Upload files less than 2MB', {
					position: toast.POSITION.TOP_RIGHT,
				});
				return;
			}
			const formData = new FormData();
			formData.append("document", fileUploaded);
			setLoaderState({ display: true });
			FileService.uploadfile(formData, token)
				.then((response) => {
					setLoaderState({ display: false });
					setEditedAnswer(prev => ({
						...prev,
						files: [
							...(prev.files || []),
							{ id: response.data.id, fileName: fileUploaded.name }
						]
					}));
				})
				.catch((error) => {
					setLoaderState({ display: false });
				});
		});
		event.target.value = null;
	};

	const requestRemoveFile = (fileId, fileIndex, fileName) => {
		setConfirmDeleteFile({ show: true, fileId, fileIndex, fileName });
	};

	const handleRemoveFile = () => {
		const { fileId, fileIndex } = confirmDeleteFile;
		setLoaderState({ display: true });
		FileService.deleteFile(token, fileId)
			.then(() => {
				setLoaderState({ display: false });
				const updatedAnswer = {
					...editedAnswer,
					files: editedAnswer.files?.filter((_, idx) => idx !== fileIndex) ?? []
				};
				saveAnswer(updatedAnswer);
			})
			.catch((error) => {
				setLoaderState({ display: false });
				toast.error(error.response?.data?.message || 'Failed to delete file', {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};
	const renderCommentIcon = (fieldId) => {
		// Find the response that corresponds to the given fieldId
		const correspondingResponse = submittedData.responses.find(
			(response) => response.formField.id === fieldId && response.remarkResponse
		);

		return (
			<>
				{correspondingResponse?.remarkResponse && (
					<div className='Comments_Response 1'>
						<span>
							<BiMessageAltDetail />
						</span>
						<p dangerouslySetInnerHTML={Utils.getHTMLMarkupFor(correspondingResponse.remarkResponse)}>
						</p>
					</div>
				)}</>
		);
	};

	const renderFileUploadAttachment = () => {
		return (
			<div className='mt-2'>
				<FileChooserAnswerField
					files={editedAnswer.files}
					onFileSelect={(evt) => handleFileSelect(evt)}
					onFileDownload={(fid) => downloadFile(fid)}
					onFileDelete={(fid, fIndex, fileName) => requestRemoveFile(fid, fIndex, fileName)}
				/>
			</div>
		);
	};

	const renderFileIcon = (fieldId) => {
		// Find the response that corresponds to the given fieldId
		const correspondingResponse = submittedData.responses.find(
			(response) => response.formField.id === fieldId
		);

		return (
			<FileListPanel isSubmittedResponseView={true}
				index={fieldId}
				files={correspondingResponse?.files}
				onDownloadFile={(fileId) => downloadFile(fileId)}
				onFilePreview={(fileId, fileName) => onFilePreview(fileId, fileName)}
				onReadOCRData={(fileId) => onReadOCRData(fileId, correspondingResponse.formField)}
			/>
		);
	}

	const renderCanvasFormHeader = () => (
		<div className='builder-canvas__form-header preview-form-header pt-4'>
			<div className='submitted-response-meta'>
				{form?.assessment?.lastSubmissionDate && <div className='Department_Namebox' >
					<span>Expiry :</span> {moment(form?.assessment?.lastSubmissionDate, 'YYYY-MM-DD').format("D MMMM, YYYY")}
				</div>}
				{renderLocationSelector()}
			</div>
			<h1>{form.name}</h1>
			{form.description && <p className='builder-text-secondary' style={{ whiteSpace: 'pre-line' }}>{form.description}</p>}
		</div>
	);

	const renderRootFieldsCard = (fieldsList) => (
		<div className='builder-section-card builder-section-card--root'>
			<div className='builder-section-card__header'>
				<span className='builder-section-card__title'>Questions</span>
				<span className='builder-section-card__count'>{fieldsList.length}</span>
			</div>
			<div className='builder-section-card__body'>
				<div className='builder-field-list'>
					{
						fieldsList.map((field, index) => field && !field.hide_alt && renderQuestionCard(field, index, fieldsList))
					}
				</div>
			</div>
		</div>
	);

	const renderSectionView = () => {
		return (
			<>
				{renderCanvasFormHeader()}
				<div className='builder-document'>
					<div id={activeSection?.id ? `section-${activeSection.id}` : undefined}
						className='builder-section-card builder-section-card--active'
						style={activeSection?.id ? { scrollMarginTop: '90px' } : undefined}>
						<div className='builder-section-card__header'>
							<span className='builder-section-card__title'>
								{activeSection?.id > 0 ? `${activeSectionIndex}. ${activeSection?.title}` : form.name}
							</span>
						</div>
						{activeSection?.id > 0 && activeSection.description &&
							<div className='px-4 pt-2'>
								<p className='preview-section-desc'>{activeSection.description}</p>
							</div>
						}
						<div className='builder-section-card__body'>
							{activeSection?.fields?.length > 0 && renderFieldList(activeSection.fields)}
							{
								activeSection?.subSections?.length > 0 &&
								<div className='builder-document__subsections'>
									{renderSubsectionCards(activeSection?.subSections, activeSectionIndex)}
								</div>
							}
						</div>
					</div>
				</div>
			</>
		)
	}

	const renderConsolidatedView = () => {
		return (
			<>
				{renderCanvasFormHeader()}
				<div className='builder-document'>
					{
						form?.fields?.some(field => field !== null) &&
						renderRootFieldsCard(form.fields)
					}
					{
						form?.sections?.length > 0 &&
						renderTopLevelSectionCards(form?.sections)
					}
				</div>
			</>
		)
	}

	const renderTopLevelSectionCards = (sections) => {
		return sections?.filter((section) => !section.hide_alt).map((section, index) => (
			<div className='builder-document__section-block' key={`section-block-${section.id}`}>
				<div id={`section-${section.id}`} className='builder-section-card' style={{ scrollMarginTop: '90px' }}>
					<div className='builder-section-card__header'>
						<span className='builder-section-card__title'>
							{index + 1}. {section.title}
						</span>
						{section.description &&
							<OverlayTrigger
								placement="top"
								overlay={<Tooltip id={`tooltip-${section.id}`} className="custom-tooltip">{section.description}</Tooltip>}
							>
								<span className="builder-section-card__count">
									<BsInfoCircle />
								</span>
							</OverlayTrigger>
						}
					</div>
					<div className='builder-section-card__body'>
						{section?.fields?.length > 0 && renderFieldList(section.fields)}
						{
							section?.subSections?.length > 0 &&
							<div className='builder-document__subsections'>
								{renderSubsectionCards(section?.subSections, `${index + 1}`)}
							</div>
						}
					</div>
				</div>
			</div>
		));
	};

	const getAnswerToRender = (answer, fieldType) => {
		if (fieldType === QuestionType.TEXT_BOX) {
			return <span dangerouslySetInnerHTML={Utils.getHTMLMarkupFor(answer)}></span>
		}
		return answer;
	};

	const renderUserInfo = (comment) => {
		if (comment?.reviewedBy) {
			if (comment?.reviewedBy?.reviewLevel) {
				return `${comment.reviewedBy?.fullName} (${Utils.getOrdinalString(comment.reviewedBy.reviewLevel)} level)`;
			} else {
				return `${comment.reviewedBy?.fullName}`;
			}
		} else {
			return comment?.dataOwner ? `${comment.dataOwner} (Data Owner)` : comment?.dataOwner;
		}
	};

	return (
		<div className='builder-shell preview-shell submitted-response-shell'
			style={{
				'--builder-sidebar-width': sidebarOpen ? `${widths.sidebar}px` : '0px',
				'--builder-panel-width': '0px',
			}}>
			<div className='builder-shell__body'>
				{
					sidebarOpen &&
					<ResizablePanel
						id="submitted-response-sidebar"
						width={widths.sidebar}
						onResize={setSidebarWidth}
						side="left">
						<SubmittedResponseTreeNavigation
							name={form.name}
							fields={form.fields}
							sections={form.sections}
							currentSectionId={activeSection?.id ?? 0}
							submittedData={submittedData}
							reviewComments={reviewComments}
							onSelectRootFields={() => setActiveSection({ fields: form.fields, subSections: [] })}
							onSelectSection={(sectionId) => {
								changeSection(sectionId);
								scrollToSection(sectionId);
							}}
							onSelectSubSection={(subSectionId) => {
								changeSubSection(subSectionId);
								scrollToSection(subSectionId);
							}}
							onSelectQuestion={(fieldId) => scrollToField(fieldId)}
						/>
					</ResizablePanel>
				}
				<div className='builder-shell__canvas-wrap'>
					<div className='builder-canvas'>
						<div className='builder-canvas__inner'>
							{
								isSinglePageView ? renderConsolidatedView() : renderSectionView()
							}
							<div className='PS_Qus_Card_footer p-3 text-center'>
								{
									activeSectionIndex >= 1 &&
									<button className='btn btn-secondary px-4 mx-2 sbmBtn' onClick={() => previousSection()}>Back</button>
								}
								{
									activeSectionIndex < form.sections?.length &&
									<button className='btn btn-primary px-4 mx-2 sbmBtn' onClick={() => nextSection()}>Next</button>
								}
								{
									!isFormInReview &&
									submittedData?.status === FormStatus.CORRECTION_PENDING &&
									!editedAnswer.formField.id &&
									<button className='btn btn-primary px-4 mx-2 sbmBtn'
										onClick={() => {
											setEditedAnswer({ formField: { id: 0 } });
											onSubmit();
										}}>Submit</button>
								}
							</div>
						</div>
					</div>
				</div>
			</div>
			<FormFooter />
			{
				openReviewComment.responseId > 0 &&
				<ReviewCommentModal
					modalShow={true}
					data={openReviewComment}
					callbackModal={(val) => reviewCommentModalCallback(val)} />
			}
			<ConfirmDialog
				title="Delete File"
				open={confirmDeleteFile.show}
				setOpen={(show) => setConfirmDeleteFile(prev => ({ ...prev, show }))}
				onConfirm={() => handleRemoveFile()}
			>
				<div className="text-center">Are you sure you want to delete <strong>{confirmDeleteFile.fileName}</strong>?</div>
			</ConfirmDialog>
		</div>
	);
};

export default SubmittedResponseView;